import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChatButton } from '@/components/AIChat';
import { toast } from 'sonner';
import { AlertCircle, Sparkles } from 'lucide-react';
import FirstTimePromo from '@/components/FirstTimePromo';
import { Button } from '@/components/ui/button';

// Import modular dashboard components
import {
  DashboardHeader,
  TrackSelector,
  QuickActions,
  CognitiveAnalysis,
  StatsOverview,
  GamificationSection,
  MobileNavigation
} from '@/components/dashboard';

// Demo data for guest users to showcase platform capabilities
const DEMO_STATS = {
  total_attempts: 47,
  average_score: 78,
  total_questions: 312,
  total_correct: 243
};

const DEMO_COGNITIVE_STATS = {
  has_data: true,
  cognitive_stats: {
    recall: { percentage: 82, correct: 45, total: 55 },
    apply: { percentage: 71, correct: 38, total: 54 },
    analyze: { percentage: 65, correct: 28, total: 43 }
  },
  recommendation: "Focus on 'Analyze' level questions to improve critical thinking skills for exam success."
};

const DEMO_GAMIFICATION = {
  badges: [
    { name: 'First Steps', icon: '🎯', description: 'Completed your first training session' },
    { name: 'Quick Learner', icon: '⚡', description: 'Answered 50 questions correctly' },
    { name: 'Streak Starter', icon: '🔥', description: 'Maintained a 3-day study streak' }
  ],
  streak: { current: 5, longest: 12 },
  nextBadges: [
    { name: 'Knowledge Master', icon: '📚', current: 243, target: 500, percent: 49 }
  ],
  questionsAnswered: 312
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setUser, logout, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [cognitiveStats, setCognitiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questionLanguage, setQuestionLanguage] = useState(user?.question_language || 'en');

  // Get user's purchased/accessible tracks from track_access
  const userTrackAccess = user?.track_access || [];
  const isAdmin = user?.role === 'admin';
  const hasCurrentTrackAccess = userTrackAccess.some(
    t => t.track === user?.selected_track && t.is_active
  );
  const hasPremiumAccess = !user?.is_guest && (isAdmin || hasCurrentTrackAccess);

  // Handle track change
  const handleTrackChange = async (track) => {
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ selected_track: track })
      });
      
      if (response.ok) {
        toast.success(`Switched to ${track}`);
        window.location.reload();
      }
    } catch (error) {
      toast.error('Failed to switch track');
    }
  };

  // Handle language change
  const handleLanguageChange = async (lang) => {
    try {
      const response = await fetch(`${API}/auth/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ language: lang })
      });
      
      if (response.ok) {
        setQuestionLanguage(lang);
        if (user) {
          setUser({ ...user, question_language: lang });
        }
        const langNames = {
          en: 'English', de: 'German', es: 'Spanish', fr: 'French',
          zh: 'Chinese', ru: 'Russian', it: 'Italian', pl: 'Polish',
          sv: 'Swedish', pt: 'Portuguese', nl: 'Dutch', tr: 'Turkish',
          ar: 'Arabic', hi: 'Hindi'
        };
        toast.success(`Language changed to ${langNames[lang] || lang}`);
      } else {
        toast.error('Failed to update language');
      }
    } catch (error) {
      toast.error('Failed to update language');
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Sync language with user preference
  useEffect(() => {
    if (user?.question_language) {
      setQuestionLanguage(user.question_language);
    }
  }, [user]);

  // Fetch stats - use demo data for guests
  useEffect(() => {
    const fetchStats = async () => {
      // For guests, use demo data to showcase platform
      if (user?.is_guest) {
        setStats(DEMO_STATS);
        setLoading(false);
        return;
      }
      
      try {
        // Use user-specific stats endpoint that filters by selected_track/course_id
        const response = await fetch(`${API}/stats/user`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          // Map API response to expected format
          setStats({
            total_attempts: data.total_attempts || 0,
            average_score: data.average_score || 0,
            total_questions: data.total_questions_answered || 0,
            total_correct: Math.round((data.correct_rate || 0) * (data.total_questions_answered || 0) / 100)
          });
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user, getAuthHeaders]);

  // Fetch gamification data - use demo data for guests
  useEffect(() => {
    const fetchGamification = async () => {
      // For guests, use demo data to showcase platform
      if (user?.is_guest) {
        setGamification(DEMO_GAMIFICATION);
        return;
      }
      
      try {
        const [badgesRes, progressRes] = await Promise.all([
          fetch(`${API}/gamification/badges`, {
            credentials: 'include',
            headers: getAuthHeaders()
          }),
          fetch(`${API}/gamification/progress`, {
            credentials: 'include',
            headers: getAuthHeaders()
          })
        ]);

        const badges = badgesRes.ok ? await badgesRes.json() : { badges: [] };
        const progress = progressRes.ok ? await progressRes.json() : {};

        setGamification({
          badges: badges.badges || [],
          streak: progress.streak || { current: 0, longest: 0 },
          nextBadges: progress.next_badges || [],
          questionsAnswered: progress.questions_answered || 0
        });
      } catch (error) {
        console.error('Failed to fetch gamification:', error);
      }
    };
    
    fetchGamification();
  }, [user, getAuthHeaders]);

  // Fetch cognitive stats - use demo data for guests
  useEffect(() => {
    const fetchCognitiveStats = async () => {
      // For guests, use demo data to showcase platform
      if (user?.is_guest) {
        setCognitiveStats(DEMO_COGNITIVE_STATS);
        return;
      }
      
      try {
        const response = await fetch(`${API}/exams/cognitive-stats`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setCognitiveStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch cognitive stats:', error);
      }
    };
    
    fetchCognitiveStats();
  }, [user, getAuthHeaders]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20 md:pb-0">
      {/* First-Time User Promotion */}
      {user?.is_guest && <FirstTimePromo />}
      
      {/* Header */}
      <DashboardHeader user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section with Language Selector */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Welcome back, {user?.name || 'User'}!
              </h1>
              {user?.is_guest && (
                <span className="flex items-center gap-2 text-sm text-orange-600 mt-1">
                  <AlertCircle className="w-4 h-4" />
                  Guest access: {Math.max(0, 50 - (user?.questions_answered || 0))} questions remaining
                </span>
              )}
            </div>
            
            {/* Language Selector with Prominent USP */}
            <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-[#0047AB]/5 to-[#0047AB]/10 rounded-xl border border-[#0047AB]/20">
              <div className="hidden sm:block">
                <p className="text-sm font-bold text-[#0047AB]">
                  Study in your language.
                </p>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                  Think in exam logic!
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Select value={questionLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-[150px] h-10 bg-white dark:bg-slate-800 border-[#0047AB]/30" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="es">Espanol</SelectItem>
                    <SelectItem value="fr">Francais</SelectItem>
                    <SelectItem value="zh">Chinese</SelectItem>
                    <SelectItem value="ru">Russian</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="pl">Polski</SelectItem>
                    <SelectItem value="sv">Svenska</SelectItem>
                    <SelectItem value="pt">Portugues</SelectItem>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="tr">Turkce</SelectItem>
                    <SelectItem value="ar">Arabic</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="cs">Cestina</SelectItem>
                    <SelectItem value="ro">Romana</SelectItem>
                    <SelectItem value="el">Ellhnika</SelectItem>
                    <SelectItem value="da">Dansk</SelectItem>
                    <SelectItem value="no">Norsk</SelectItem>
                    <SelectItem value="fi">Suomi</SelectItem>
                    <SelectItem value="bg">Bulgarski</SelectItem>
                    <SelectItem value="hr">Hrvatski</SelectItem>
                    <SelectItem value="sk">Slovencina</SelectItem>
                    <SelectItem value="sr">Srpski</SelectItem>
                    <SelectItem value="hu">Magyar</SelectItem>
                    <SelectItem value="uk">Ukrainska</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-500 sm:hidden">25 languages available</span>
              </div>
            </div>
          </div>
        </div>

        {/* 1. Track Selector */}
        <TrackSelector 
          user={user} 
          hasPremiumAccess={hasPremiumAccess}
          onTrackChange={handleTrackChange}
        />

        {/* 2. Quick Actions */}
        <QuickActions user={user} hasPremiumAccess={hasPremiumAccess} />

        {/* Demo Data Banner for Guests */}
        {user?.is_guest && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Preview Mode - Sample Data Displayed
                </p>
                <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-1">
                  The statistics and progress shown below are examples. Register to track your real learning journey and unlock all features!
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/register')}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                data-testid="demo-register-btn"
              >
                Register Now
              </Button>
            </div>
          </div>
        )}

        {/* 3. Cognitive Analysis - Show for all users (demo for guests) */}
        <CognitiveAnalysis cognitiveStats={cognitiveStats} isDemo={user?.is_guest} />

        {/* 4. Stats Overview */}
        <StatsOverview stats={stats} />

        {/* 5. Gamification Section - Show for all users (demo for guests) */}
        <GamificationSection gamification={gamification} isDemo={user?.is_guest} />
      </main>

      {/* AI Chat Button */}
      <ChatButton />

      {/* Mobile Navigation */}
      <MobileNavigation />
    </div>
  );
};

export default Dashboard;
